// Bridge to the native purchase flow exposed by the Flutter WebView wrapper
// (see mobile/lib/main.dart, JavaScriptChannel "IkonetNative"). Subscriptions
// are sold exclusively through Apple/Google in-app purchase (StoreKit / Play
// Billing directly, no third-party billing service), so this is the only
// place the web app ever asks for money — there is no web checkout. The
// native shell only knows how to talk to the store; it has no notion of our
// backend or which company to credit, so every purchase/restore result it
// emits still needs to be sent to POST /api/payments/validate-iap by the
// caller (see UpgradePage.js) using the already-authenticated web session.
const NATIVE_CHANNEL = 'IkonetNative';
const NATIVE_EVENT = 'ikonet-native';

export function isNativeApp() {
  return typeof window !== 'undefined' && !!window[NATIVE_CHANNEL];
}

function postToNative(payload) {
  if (!isNativeApp()) return false;
  window[NATIVE_CHANNEL].postMessage(JSON.stringify(payload));
  return true;
}

function waitForNativeEvent(matchType, timeoutMs, match) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      window.removeEventListener(NATIVE_EVENT, handler);
      reject(new Error('native_timeout'));
    }, timeoutMs);
    function handler(e) {
      const detail = e.detail;
      if (!detail || detail.type !== matchType) return;
      if (match && !match(detail)) return;
      clearTimeout(timer);
      window.removeEventListener(NATIVE_EVENT, handler);
      resolve(detail);
    }
    window.addEventListener(NATIVE_EVENT, handler);
  });
}

// No-ops in the current native shell (kept so older/newer app builds calling
// this stay harmless): purchases are attributed to a company server-side via
// the authenticated session at validation time, not via a native SDK login.
export function notifyNativeLogin(companyId) {
  if (companyId) postToNative({ type: 'login', companyId });
}

export function notifyNativeLogout() {
  postToNative({ type: 'logout' });
}

// planId must match a product identifier configured in App Store Connect /
// Google Play Console (see backend/server.py IAP_PRODUCT_MAP), e.g.
// "standard_monthly". Resolves with the raw native purchase result —
// { success, planId, transactionId, platform, receiptData | purchaseToken } —
// the caller still has to send this to billingApi.validateIAP to apply it.
export function purchasePlanNative(planId) {
  if (!isNativeApp()) return Promise.reject(new Error('not_native'));
  const result = waitForNativeEvent('purchase_result', 120000, (d) => d.planId === planId);
  postToNative({ type: 'purchase', planId });
  return result;
}

// StoreKit/Play Billing can replay more than one past purchase on restore;
// resolves with { success, purchases: [...] } once the native restore call
// finishes, each entry shaped like a purchasePlanNative() result and still
// needing its own billingApi.validateIAP call.
export function restorePurchasesNative() {
  if (!isNativeApp()) return Promise.reject(new Error('not_native'));
  return new Promise((resolve, reject) => {
    const purchases = [];
    let timer;

    function purchaseHandler(e) {
      const detail = e.detail;
      if (detail && detail.type === 'purchase_result' && detail.success) {
        purchases.push(detail);
      }
    }
    function cleanup() {
      clearTimeout(timer);
      window.removeEventListener(NATIVE_EVENT, purchaseHandler);
      window.removeEventListener(NATIVE_EVENT, ackHandler);
    }
    function ackHandler(e) {
      const detail = e.detail;
      if (!detail || detail.type !== 'restore_result') return;
      if (!detail.success) {
        cleanup();
        reject(new Error(detail.error || 'restore_failed'));
        return;
      }
      // Give any in-flight purchase_result events (past purchases the store is
      // still replaying) a moment to arrive before resolving with what we have.
      clearTimeout(timer);
      timer = setTimeout(() => {
        cleanup();
        resolve({ success: true, purchases });
      }, 1500);
    }

    timer = setTimeout(() => {
      cleanup();
      reject(new Error('native_timeout'));
    }, 30000);
    window.addEventListener(NATIVE_EVENT, purchaseHandler);
    window.addEventListener(NATIVE_EVENT, ackHandler);
    postToNative({ type: 'restore' });
  });
}
